import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ModalActionFooter from '../ModalActionFooter/ModalActionFooter';
import { useAppDispatch } from '../../store/hooks';
import { setPods } from '../../store/slices/podsSlice';
import { setEpics } from '../../store/slices/epicsSlice';
import { setCategories } from '../../store/slices/categoriesSlice';
import { setSprintSettings, DEFAULT_SPRINT_LENGTH, DEFAULT_TOTAL_SPRINTS, setWorkstreamSprints } from '../../store/slices/sprintsSlice';
import { setEpicWeights, setStoryWeights, defaultEpicWeights, defaultStoryWeights } from '../../store/slices/healthConfigSlice';
import { setAllEpicStatuses, setAllStoryStatuses, defaultEpicStatuses, defaultStoryStatuses } from '../../store/slices/stateManagementSlice';
import { setWorkstreams } from '../../store/slices/workstreamsSlice';
import { demoScenarios } from '../../data/demoScenarios';

interface ScenarioPickerDialogProps {
  open: boolean;
  onClose: () => void;
}

const ScenarioPickerDialog: React.FC<ScenarioPickerDialogProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);

  const handleClose = () => {
    setSelectedScenarioId(null);
    onClose();
  };

  const handleLoadScenario = () => {
    const scenario = demoScenarios.find((s) => s.id === selectedScenarioId);
    if (!scenario) return;

    if (scenario.data) {
      const epicsWithStories = scenario.data.epics.map((epic) => ({
        ...epic,
        stories: scenario.data!.stories.filter((s) => s.epicId === epic.id),
      }));
      dispatch(setCategories(scenario.data.categories));
      dispatch(setPods(scenario.data.pods));
      dispatch(setEpics(epicsWithStories));
      dispatch(setSprintSettings(scenario.data.sprintSettings));
      dispatch(setEpicWeights(scenario.data.healthConfig.epicWeights));
      dispatch(setStoryWeights(scenario.data.healthConfig.storyWeights));
      dispatch(setAllEpicStatuses(scenario.data.stateManagement.epicStatuses));
      dispatch(setAllStoryStatuses(scenario.data.stateManagement.storyStatuses));
      dispatch(setWorkstreams(scenario.data.workstreams));
      dispatch(setWorkstreamSprints(scenario.data.workstreamSprints ?? []));
    } else {
      // Scenario 0: start from scratch — reset everything to defaults
      dispatch(setPods([]));
      dispatch(setCategories([]));
      dispatch(setEpics([]));
      const nextMonday = new Date();
      const day = nextMonday.getDay();
      const diff = day === 1 ? 0 : (8 - day) % 7;
      nextMonday.setDate(nextMonday.getDate() + diff);
      nextMonday.setHours(0, 0, 0, 0);
      dispatch(setSprintSettings({
        startDate: nextMonday.toISOString(),
        sprintLength: DEFAULT_SPRINT_LENGTH,
        totalSprints: DEFAULT_TOTAL_SPRINTS,
      }));
      dispatch(setEpicWeights(defaultEpicWeights));
      dispatch(setStoryWeights(defaultStoryWeights));
      dispatch(setAllEpicStatuses(defaultEpicStatuses));
      dispatch(setAllStoryStatuses(defaultStoryStatuses));
      dispatch(setWorkstreams([]));
      dispatch(setWorkstreamSprints([]));
    }

    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Load Demo Scenario</Typography>
          <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a scenario then click <strong>Load Scenario</strong> to populate the app with demo data.
        </Typography>
        <Stack spacing={1.5}>
          {demoScenarios.map((scenario) => {
            const isSelected = selectedScenarioId === scenario.id;
            return (
              <Paper
                key={scenario.id}
                variant="outlined"
                onClick={() => setSelectedScenarioId(scenario.id)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'action.selected' : 'background.paper',
                  '&:hover': { borderColor: 'primary.light' },
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Radio
                    checked={isSelected}
                    size="small"
                    sx={{ mt: -0.25, p: 0.5 }}
                    inputProps={{ 'aria-label': scenario.title, readOnly: true }}
                    tabIndex={-1}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      {scenario.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {scenario.description}
                    </Typography>
                    {scenario.highlights.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {scenario.highlights.map((highlight) => (
                          <Chip key={highlight} label={highlight} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <ModalActionFooter
          onCancel={handleClose}
          onSave={handleLoadScenario}
          saveLabel="Load Scenario"
          saveDisabled={selectedScenarioId === null}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioPickerDialog;
